import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Users, Mic } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { API } from "@/App";
import Header from "@/components/Header";

export default function LiveBroadcast() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [showStart, setShowStart] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/live`, { credentials: "include" });
      if (res.ok) setSessions(await res.json());
    } catch (e) { toast.error("Failed to load live sessions"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 5000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const startLive = async (title, type) => {
    try {
      const res = await fetch(`${API}/live/start`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, type }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowStart(false);
        toast.success("You're live! Starting camera…");
        navigate(`/live-rtc/${data.session_id}`);
      } else {
        const err = await res.json();
        toast.error(typeof err.detail === "string" ? err.detail : "Failed");
      }
    } catch (e) { toast.error("Failed to start"); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #000044 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold glitter-text" data-testid="live-page-title">Live</h1>
          <button onClick={() => setShowStart(true)} className="myspace-btn-primary flex items-center gap-2" data-testid="go-live-btn">
            <Radio className="w-4 h-4" /> Go Live
          </button>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-12 h-12 border-4 border-[#FF00FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="myspace-card text-center py-16" data-testid="no-live-sessions">
              <Radio className="w-16 h-16 mx-auto mb-4 text-[#FF0000] opacity-50" />
              <h2 className="text-xl font-bold text-[#FF66FF] mb-2">No One's Live</h2>
              <p className="text-[#99CCFF] mb-4">Be the first to go live!</p>
              <button onClick={() => setShowStart(true)} className="myspace-btn-primary" data-testid="go-live-empty-btn">Go Live</button>
            </div>
          ) : sessions.map(s => (
            <div
              key={s.session_id}
              className="myspace-card cursor-pointer hover:border-[#FF0000] transition-all"
              onClick={() => navigate(`/live-rtc/${s.session_id}`)}
              data-testid={`live-session-${s.session_id}`}
            >
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-[#FF0000] text-white text-xs font-bold animate-pulse flex-shrink-0">LIVE</span>
                <div className="flex-1">
                  <p className="text-[#00FFFF] font-bold">{s.title}</p>
                  <p className="text-xs text-[#FF66FF]">{s.host_name} {s.type === "karaoke" ? "- Karaoke" : ""}</p>
                </div>
                <span className="text-sm text-[#6699CC] flex items-center gap-1">
                  <Users className="w-4 h-4" /> {s.viewer_count || 0}
                </span>
              </div>
            </div>
          ))}
        </div>
      </main>

      <AnimatePresence>
        {showStart && (<StartLiveModal onClose={() => setShowStart(false)} onStart={startLive} />)}
      </AnimatePresence>
    </div>
  );
}

function StartLiveModal({ onClose, onStart }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("broadcast");

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="myspace-card w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="start-live-modal">
        <h2 className="text-xl font-bold text-[#00FFFF] mb-4">Go Live</h2>
        <p className="text-xs text-[#99CCFF] mb-4">
          We'll request camera + mic access on the next screen. Real-time peer-to-peer broadcast — no servers in between.
        </p>
        <div className="space-y-4">
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Broadcast title" className="myspace-input" data-testid="live-title-input" />
          <div className="flex gap-3">
            <button onClick={() => setType("broadcast")}
              className={`flex-1 p-3 border-2 text-center ${type === "broadcast" ? "border-[#FF00FF] bg-[rgba(255,0,255,0.1)]" : "border-[#6600CC]"}`}
              data-testid="type-broadcast"
            >
              <Radio className="w-6 h-6 mx-auto mb-1 text-[#FF00FF]" />
              <p className="text-sm text-[#FF66FF]">Broadcast</p>
            </button>
            <button onClick={() => setType("karaoke")}
              className={`flex-1 p-3 border-2 text-center ${type === "karaoke" ? "border-[#FF00FF] bg-[rgba(255,0,255,0.1)]" : "border-[#6600CC]"}`}
              data-testid="type-karaoke"
            >
              <Mic className="w-6 h-6 mx-auto mb-1 text-[#FF00FF]" />
              <p className="text-sm text-[#FF66FF]">Karaoke</p>
            </button>
          </div>
          <button onClick={() => onStart(title.trim() || "My Live", type)} className="myspace-btn-primary w-full" data-testid="start-live-btn">
            Start Broadcasting
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
